import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { prettifyApiError } from "@/shared/api";
import { usePermessi } from "@/shared/auth/permessi";
import { ErrorBanner } from "@/shared/ui/ErrorBanner";
import { usePersistedState } from "@/shared/usePersistedState";
import { useIsMobile } from "@/shared/useMediaQuery";
import { usePassoIndietro } from "@/shared/usePassoIndietro";
import { fetchMagazzini, SLOT_RESI, type MagazzinoRiepilogo } from "./api";
import { Cronologia } from "./components/Cronologia";
import { MagazziniLista } from "./components/MagazziniLista";
import { SchedaMagazzino } from "./components/SchedaMagazzino";

/** L'intestazione è scritta qui e non con ``PageHeader``: il suo sottotitolo è
 *  ``text-ink-2``, e questa pagina vuole il testo nero (PRODUCT.md).
 *  ponytail: quando ``PageHeader`` passa a ``text-ink``, si torna a lui. */
export function RitiriPage() {
  const [magazzini, setMagazzini] = useState<MagazzinoRiepilogo[] | null>(null);
  const [errore, setErrore] = useState<string | null>(null);
  const [slot, setSlot] = usePersistedState<string>("om.ritiri.slot", "itamoby");
  const { puoScrivere, inCaricamento } = usePermessi();
  // Il motivo si scrive solo a permessi caricati: mentre /me è in volo i tasti
  // sono già spenti, e l'avviso sfarfallerebbe.
  const soloLettura = !inCaricamento && !puoScrivere("spedizioni");
  // Telefono: prima l'elenco dei magazzini, un tocco apre la scheda a tutta
  // pagina (col «indietro» di Android che torna all'elenco). Sul computer le
  // due colonne restano come sono.
  const isMobile = useIsMobile();
  const scheda = usePassoIndietro(isMobile, "omScheda");
  const mostraLista = !isMobile || !scheda.aperto;
  const mostraScheda = !isMobile || scheda.aperto;
  const mainRef = useRef<HTMLElement>(null);
  // Elenco e scheda condividono lo scorrimento: la scheda si apre dall'alto.
  useEffect(() => {
    if (isMobile) mainRef.current?.scrollTo?.({ top: 0 });
  }, [isMobile, scheda.aperto]);

  const ricarica = useCallback(async () => {
    try {
      setMagazzini(await fetchMagazzini());
      setErrore(null);
    } catch (e) {
      setErrore(prettifyApiError(e));
    }
  }, []);

  useEffect(() => {
    void ricarica();
  }, [ricarica]);

  const scelto =
    slot === SLOT_RESI
      ? SLOT_RESI
      : (magazzini?.find((m) => m.slot === slot)?.slot ?? magazzini?.[0]?.slot ?? null);

  return (
    // AppShell impila questa pagina in una colonna `overflow-hidden`: senza
    // `flex-1 overflow-y-auto` un contenuto più alto della finestra viene
    // TAGLIATO (cronologia irraggiungibile, «Prenota» sparito sotto i
    // 1024px) invece di scorrere. Stesse classi di `resi/ResiPage.tsx`.
    <main
      ref={mainRef}
      className="flex-1 overflow-y-auto px-7 pt-[22px] pb-8 scroll-paper max-md:px-3 max-md:pt-3"
    >
      <header className="mb-4 max-md:mb-3">
        {/* Sul telefono il titolo lo porta la barra in alto. */}
        <h1 className="font-display text-[30px] font-bold leading-[1.1] tracking-[-0.02em] text-ink max-md:sr-only">
          Ritiri
        </h1>
        <p className="mt-1 text-[13px] text-ink max-md:mt-0 max-md:text-[14px]">
          Corriere SDA tramite Bracchi · una prenotazione al giorno per magazzino
        </p>
        {soloLettura && (
          <p className="mt-2 inline-block rounded-lg bg-amber-soft px-2 py-1 text-[12px] font-semibold text-ink">
            Solo consultazione: per prenotare servono i permessi dell'area spedizioni.
          </p>
        )}
      </header>
      <ErrorBanner message={errore} className="mb-3" />
      {errore && (
        <button
          type="button"
          onClick={() => void ricarica()}
          className="mb-3 rounded-full border border-line-2 px-3 py-1.5 text-[12.5px] font-bold text-ink"
        >
          Riprova
        </button>
      )}
      <div className="grid grid-cols-[292px_minmax(0,1fr)] items-start gap-4 max-lg:grid-cols-1">
        {/* Sempre presente, anche a vuoto: la griglia mette i figli nelle
            celle nell'ORDINE in cui esistono nel DOM. Se questo slot non
            rendesse NULLA (errore di caricamento, niente lista né "in
            caricamento"), il pannello Resi/scheda sotto diventerebbe il
            PRIMO figlio e finirebbe lui nella colonna stretta da 292px. */}
        <div className={mostraLista ? undefined : "hidden"}>
          {magazzini ? (
            <MagazziniLista
              magazzini={magazzini}
              selezionato={scelto}
              onScegli={(s) => {
                setSlot(s);
                if (isMobile) scheda.apri();
              }}
            />
          ) : (
            !errore && <p className="text-[12.5px] text-ink">Caricamento magazzini…</p>
          )}
        </div>
        {isMobile && scheda.aperto && (
          <button
            type="button"
            onClick={scheda.chiudi}
            className="om-press -mb-1 flex h-11 items-center gap-1 self-start rounded-full pr-4 text-[15px] font-semibold text-ink"
          >
            <svg
              className="h-5 w-5"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M15 18l-6-6 6-6" />
            </svg>
            Tutti i magazzini
          </button>
        )}
        {!mostraScheda ? null : scelto === SLOT_RESI ? (
          <section className="rounded-2xl border border-line bg-card shadow-[0_1px_2px_rgba(31,27,22,0.04)] p-5 text-ink">
            <h2 className="mb-1 font-display text-[22px] font-bold">Ritiri dai clienti</h2>
            <p className="mb-3 text-[12.5px]">
              Il ritiro di un reso si prenota dal dialog del reso o dalla{" "}
              <Link to="/resi" className="font-bold underline">
                pagina Resi
              </Link>
              , dove ogni reso con l'etichetta pronta ha il suo tasto.
            </p>
            <Cronologia filtro={{ tipo: "reso" }} onCambiata={() => void ricarica()} />
          </section>
        ) : scelto ? (
          <SchedaMagazzino key={scelto} slot={scelto} onCambiata={() => void ricarica()} />
        ) : null}
      </div>
    </main>
  );
}
